import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Disc3, Filter, Cog, CircleDot, Waypoints, Wrench, Thermometer, Wind, Zap, Lightbulb,
  Layers, ChevronDown, PlusCircle, CircleGauge,
} from "lucide-react";
import { PARTS_CATALOGUE } from "@/lib/constants";

const ICONS = {
  freinage: Disc3, filtration: Filter, distribution: Cog, embrayage: CircleDot,
  suspension: Waypoints, moteur: Wrench, refroidissement: Thermometer, echappement: Wind,
  electrique: Zap, eclairage: Lightbulb,
};

export default function PartsCatalogue({ vehicle, onPick }) {
  const [openGroup, setOpenGroup] = useState("freinage");
  if (!vehicle?.found) return null;
  const tires = Array.isArray(vehicle.tires) ? vehicle.tires : [];

  return (
    <div className="mt-4 rounded-xl border border-blue-500/30 bg-blue-500/5 p-4" data-testid="parts-catalogue">
      <div className="flex items-center gap-2 mb-1">
        <Layers className="h-4 w-4 text-blue-600" />
        <span className="font-semibold text-sm">Pièces compatibles — catalogue TecDoc</span>
      </div>
      <p className="text-xs text-muted-foreground mb-3">
        Familles de pièces pour ce véhicule. Cliquez pour pré-remplir votre demande.
      </p>

      <div className="flex flex-wrap gap-2 mb-4">
        {vehicle.k_type && <span data-testid="ktype-badge" className="px-2 py-1 rounded-md bg-blue-600 text-white text-[11px] font-mono">K-Type : {vehicle.k_type}</span>}
        {vehicle.tecdoc_car_id && <span className="px-2 py-1 rounded-md bg-slate-500/10 text-slate-600 text-[11px] font-mono">TecDoc car : {vehicle.tecdoc_car_id}</span>}
        {vehicle.tecdoc_manu_id && <span className="px-2 py-1 rounded-md bg-slate-500/10 text-slate-600 text-[11px] font-mono">Constructeur : {vehicle.tecdoc_manu_id}</span>}
      </div>

      {tires.length > 0 && (
        <div className="mb-4 rounded-lg bg-card border border-border p-3">
          <div className="flex items-center gap-2 font-medium text-sm mb-2"><CircleGauge className="h-4 w-4 text-blue-600" />Pneumatiques d'origine</div>
          <div className="flex flex-wrap gap-2">
            {tires.map((t, i) => (
              <button key={`${t.name}-${i}`} data-testid={`tire-${i}`} onClick={() => onPick(`Pneu ${t.name}`)}
                className="px-3 py-1.5 rounded-full text-xs font-mono border border-border bg-background hover:border-blue-400 hover:text-blue-600 transition-colors">
                {t.name}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="space-y-2">
        {PARTS_CATALOGUE.map((grp) => {
          const Icon = ICONS[grp.key] || Wrench;
          const open = openGroup === grp.key;
          return (
            <div key={grp.key} className="rounded-lg bg-card border border-border overflow-hidden">
              <button
                data-testid={`group-${grp.key}`}
                onClick={() => setOpenGroup(open ? "" : grp.key)}
                className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-muted/50 transition-colors"
              >
                <span className="h-8 w-8 rounded-lg bg-blue-500/10 text-blue-600 flex items-center justify-center"><Icon className="h-4 w-4" /></span>
                <span className="font-medium text-sm flex-1">{grp.label}</span>
                <span className="text-xs text-muted-foreground">{grp.items.length}</span>
                <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
              </button>
              <AnimatePresence initial={false}>
                {open && (
                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                    <div className="px-3 pb-3 pt-1 flex flex-wrap gap-2">
                      {grp.items.map((it) => (
                        <button
                          key={it}
                          data-testid={`part-${it}`}
                          onClick={() => onPick(it)}
                          className="group inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border border-border bg-background hover:border-blue-500 hover:bg-blue-500/5 hover:text-blue-700 transition-colors"
                        >
                          <PlusCircle className="h-3.5 w-3.5 opacity-50 group-hover:opacity-100" />{it}
                        </button>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>
    </div>
  );
}
